/**
 * Modern Bold · <mb-variant-picker> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * PDP variant resolver + UI sync. Owns:
 *   • radio change → variant lookup → updateUI (price, CTA, sticky CTA,
 *     stock urgency, swatch / size labels, SR announcements)
 *   • per-option-value availability probing (greys out unbuyable choices)
 *   • qty stepper (+ / −)
 *   • URL `?variant=` sync (replaceState)
 *   • dispatches `mb:variant-change` on the document so dependent widgets
 *     (modern-product-bundle, ...) can react without scraping the DOM
 *
 * Depends on:
 *   <product-form>        defined by modern-product-form.js — must load FIRST
 *   window.mbFormatMoney  defined by modern-bold.js — integer cents → "$x.yz"
 *
 * DOM contract (rendered by snippets/modern-product-detail.liquid plus the
 * embedded variant JSON in sections/pdp-grid-product-detail.liquid):
 *   <mb-variant-picker>
 *     <script type="application/json" data-mb-variants> [variants…]
 *     <product-form data-mb-product-form>
 *       <form>
 *         <input data-mb-variant-id>
 *         <input data-mb-qty-hidden>
 *         <input data-mb-option-input data-option-position="1|2|3"> × N
 *         <button data-mb-add>
 *     [data-mb-price] [data-mb-compare-price] [data-mb-save-badge]
 *     [data-mb-sr-status]  (aria-live)
 *     [data-mb-stock-status] [data-mb-stock-text]
 *     [data-mb-color-label] [data-mb-size-label]
 *     [data-mb-qty-display] [data-mb-qty-minus] [data-mb-qty-plus]
 *
 * Sticky CTA refs (data-mb-sticky-*) live OUTSIDE this element (the
 * sticky bar is a section sibling, position:fixed) so they're queried
 * from `document` and null-guarded throughout updateUI.
 */
if (!customElements.get('mb-variant-picker')) {
  class MBVariantPicker extends HTMLElement {
    #cleanups = [];
    #variants = [];
    #form = null;
    #refs = {};

    connectedCallback() {
      const variantDataEl = this.querySelector('[data-mb-variants]');
      if (!variantDataEl) return;
      try {
        this.#variants = JSON.parse(variantDataEl.textContent);
      } catch {
        return;
      }

      const productFormEl = this.querySelector('[data-mb-product-form]');
      if (!productFormEl) return;
      this.#form = productFormEl.querySelector('form') || productFormEl;

      this.#cacheRefs();
      this.#bindForm();
      this.#bindQty();

      // Initial pass — handles URL-preselected sold-out variants and
      // products where some option values are entirely unavailable on
      // first paint. Without this, the disabled CSS only kicks in after
      // the first user interaction.
      this.#updateOptionAvailability();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    // ── DOM cache ──────────────────────────────────────────────────────────

    #cacheRefs() {
      const $ = (sel) => this.querySelector(sel);
      this.#refs = {
        variantIdInput: this.#form.querySelector('[data-mb-variant-id]'),
        qtyHiddenInput: this.#form.querySelector('[data-mb-qty-hidden]'),
        qtyDisplayInput: $('[data-mb-qty-display]'),
        addBtn:    this.#form.querySelector('[data-mb-add]'),
        priceNow:  $('[data-mb-price]'),
        priceWas:  $('[data-mb-compare-price]'),
        saveBadge: $('[data-mb-save-badge]'),
        srStatus:  $('[data-mb-sr-status]'),
        stockEl:   $('[data-mb-stock-status]'),
        stockText: $('[data-mb-stock-text]'),
        colorLabel: $('[data-mb-color-label]'),
        sizeLabel:  $('[data-mb-size-label]'),
        // Sticky CTA — may live OUTSIDE this element (the sticky bar is
        // rendered as a section sibling, position:fixed). Querying from
        // document keeps it reachable. Null-guarded throughout updateUI.
        stickyPrice:   document.querySelector('[data-mb-sticky-price]'),
        stickyWas:     document.querySelector('[data-mb-sticky-was]'),
        stickyVariant: document.querySelector('[data-mb-sticky-variant]'),
        stickyAdd:     document.querySelector('[data-mb-sticky-add]'),
      };
    }

    // ── Variant selection ──────────────────────────────────────────────────

    #getSelectedOptions() {
      const opts = {};
      this.#form.querySelectorAll('[data-mb-option-input]:checked').forEach((inp) => {
        opts[parseInt(inp.dataset.optionPosition, 10)] = inp.value;
      });
      return opts;
    }

    #findVariant(selectedOptions) {
      return this.#variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        for (const [pos, val] of Object.entries(selectedOptions)) {
          if (opts[parseInt(pos, 10) - 1] !== val) return false;
        }
        return true;
      }) || null;
    }

    /**
     * Probe each option-value for purchasability against the current other-
     * position selections. Disabled radios get the native `disabled` attr
     * so clicks are swallowed and the strike-through CSS kicks in. The
     * currently-checked input stays in the probe (sold-out URL preselects
     * should still appear "selected + struck-through").
     */
    #updateOptionAvailability() {
      const selected = this.#getSelectedOptions();
      this.#form.querySelectorAll('[data-mb-option-input]').forEach((inp) => {
        const pos = parseInt(inp.dataset.optionPosition, 10);
        const value = inp.value;
        const probe = Object.assign({}, selected, { [pos]: value });

        const anyAvailable = this.#variants.some((v) => {
          const opts = [v.option1, v.option2, v.option3];
          for (const p in probe) {
            if (opts[parseInt(p, 10) - 1] !== probe[p]) return false;
          }
          return v.available;
        });

        inp.disabled = !anyAvailable;
        const label = inp.closest('label');
        if (label) label.classList.toggle('is-unavailable', !anyAvailable);
      });
    }

    // ── UI sync ────────────────────────────────────────────────────────────

    #setBtnText(btn, txt) {
      if (!btn) return;
      const slot = btn.querySelector('[data-mb-add-label]');
      if (slot) { slot.textContent = txt; return; }
      for (const node of btn.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()) {
          node.nodeValue = txt;
          return;
        }
      }
      btn.textContent = txt;
    }

    #updateUI(variant) {
      const r = this.#refs;
      const formatMoney = window.mbFormatMoney;

      if (!variant) {
        // Unavailable combination
        if (r.addBtn)    { r.addBtn.disabled = true;    r.addBtn.setAttribute('aria-disabled', 'true'); }
        if (r.stickyAdd) { r.stickyAdd.disabled = true; r.stickyAdd.setAttribute('aria-disabled', 'true'); }
        this.#setBtnText(r.addBtn,    'Unavailable');
        this.#setBtnText(r.stickyAdd, 'Unavailable');
        const pf = this.querySelector('product-form');
        if (pf) pf.dataset.state = 'unavailable';
        if (r.srStatus) r.srStatus.textContent = 'This combination is unavailable';
        document.dispatchEvent(new CustomEvent('mb:variant-change', { detail: { variant: null } }));
        return;
      }

      if (r.variantIdInput) r.variantIdInput.value = variant.id;

      // Price (main + sticky)
      const nowFormatted = formatMoney(variant.price);
      if (r.priceNow)    r.priceNow.textContent    = nowFormatted;
      if (r.stickyPrice) r.stickyPrice.textContent = nowFormatted;

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (onSale) {
        const wasFormatted = formatMoney(variant.compare_at_price);
        if (r.priceWas) {
          r.priceWas.hidden = false;
          const s = r.priceWas.querySelector('s');
          if (s) { s.textContent = wasFormatted; s.setAttribute('aria-label', 'Was ' + wasFormatted); }
        }
        if (r.saveBadge) {
          const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          r.saveBadge.textContent = 'SAVE ' + pct + '%';
          r.saveBadge.hidden = false;
        }
        if (r.stickyWas) {
          r.stickyWas.textContent = wasFormatted;
          r.stickyWas.hidden = false;
        }
      } else {
        if (r.priceWas)   r.priceWas.hidden = true;
        if (r.saveBadge)  r.saveBadge.hidden = true;
        if (r.stickyWas)  r.stickyWas.hidden = true;
      }

      if (r.stickyVariant && variant.title !== 'Default Title') {
        r.stickyVariant.textContent = variant.title;
      }

      // Availability
      const available = variant.available;
      if (r.addBtn) {
        r.addBtn.disabled = !available;
        r.addBtn.setAttribute('aria-disabled', String(!available));
      }
      this.#setBtnText(r.addBtn, available ? 'Add to cart' : 'Sold out');
      if (r.stickyAdd) {
        r.stickyAdd.disabled = !available;
        r.stickyAdd.setAttribute('aria-disabled', String(!available));
      }
      this.#setBtnText(r.stickyAdd, available ? 'Add to cart' : 'Sold out');

      const pf = this.querySelector('product-form');
      if (pf && pf.dataset.state !== 'loading' && pf.dataset.state !== 'added') {
        pf.dataset.state = available ? 'idle' : 'sold-out';
      }

      // Stock urgency
      if (r.stockEl && r.stockText) {
        const inv = variant.inventory_management === 'shopify' && variant.inventory_quantity;
        if (inv > 0 && inv <= 5) {
          r.stockText.innerHTML = 'Only <strong>' + inv + ' left</strong> &mdash; ships in 2 business days';
          r.stockEl.removeAttribute('hidden');
        } else {
          r.stockEl.setAttribute('hidden', '');
        }
      }

      // SR announcement — reset then set on next frame so AT re-announces
      if (r.srStatus) {
        const msg = nowFormatted + (available ? ' — In stock' : ' — Sold out');
        r.srStatus.textContent = '';
        requestAnimationFrame(() => { r.srStatus.textContent = msg; });
      }

      // Option display labels
      if (r.colorLabel) r.colorLabel.textContent = variant.option1 || '';
      if (r.sizeLabel) {
        const sizeOpts = Array.from(this.querySelectorAll('[data-mb-option-group]'));
        sizeOpts.forEach((group) => {
          const idx = parseInt(group.dataset.optionIndex, 10);
          if (idx === 1) r.sizeLabel.textContent = variant.option2 || '';
          if (idx === 2) r.sizeLabel.textContent = variant.option3 || '';
        });
      }

      // Notify dependent widgets (bundle, etc.) of the new selection.
      // Carries the resolved variant object so listeners don't need to
      // scrape the DOM or re-parse the variant cache.
      document.dispatchEvent(new CustomEvent('mb:variant-change', { detail: { variant } }));
    }

    // ── Event wiring ───────────────────────────────────────────────────────

    #bindForm() {
      const onChange = (e) => {
        const inp = e.target;
        if (!inp.matches('[data-mb-option-input]')) return;
        const variant = this.#findVariant(this.#getSelectedOptions());

        // Update selected pill styles (for :has fallback in older browsers)
        this.#form.querySelectorAll('.mb-pdp-detail__size-label').forEach((lbl) => {
          const radio = lbl.querySelector('input[type="radio"]');
          if (radio) lbl.classList.toggle('is-selected', radio.checked);
        });

        this.#updateUI(variant);
        this.#updateOptionAvailability();

        // Sync ?variant=<id> into the URL so the address bar is shareable
        // and a refresh restores the selection. replaceState (not push) so
        // browser-back doesn't have to step through every option click.
        if (variant && variant.id && window.history && window.history.replaceState) {
          try {
            const u = new URL(window.location.href);
            u.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', u.toString());
          } catch (_) { /* old browsers / opaque origins — silently skip */ }
        }
      };

      this.#form.addEventListener('change', onChange);
      this.#cleanups.push(() => this.#form.removeEventListener('change', onChange));
    }

    #bindQty() {
      const minusBtn = this.querySelector('[data-mb-qty-minus]');
      const plusBtn  = this.querySelector('[data-mb-qty-plus]');
      const display  = this.#refs.qtyDisplayInput;
      const hidden   = this.#refs.qtyHiddenInput;
      if (!minusBtn || !plusBtn || !display || !hidden) return;

      const sync = (val) => {
        const n = Math.max(1, val);
        display.value = n;
        hidden.value = n;
      };
      const onMinus  = () => sync(parseInt(display.value, 10) - 1);
      const onPlus   = () => sync(parseInt(display.value, 10) + 1);
      const onChange = () => sync(parseInt(display.value, 10));

      minusBtn.addEventListener('click', onMinus);
      plusBtn.addEventListener('click', onPlus);
      display.addEventListener('change', onChange);
      this.#cleanups.push(() => {
        minusBtn.removeEventListener('click', onMinus);
        plusBtn.removeEventListener('click', onPlus);
        display.removeEventListener('change', onChange);
      });
    }
  }
  customElements.define('mb-variant-picker', MBVariantPicker);
}
